import { apiService } from './api';
import type { Pagination, User } from '../types';
import type { EnrollmentListRow } from '../types/sessions';
import type { IdCardAudience, IdCardContext, IdCardDesign, IdCardDesignInput, IdCardDesignList } from '../types/idCards';
import { buildListParams, type ListEnrollmentsFilter } from './sessions/enrollmentsApi';

export const idCardService = {
  context: async (): Promise<IdCardContext> => (await apiService.get<IdCardContext>('/id-cards/context')).data!,

  listDesigns: async (audience: IdCardAudience, params: { page?: number; limit?: number } = {}): Promise<IdCardDesignList> =>
    (await apiService.get<IdCardDesignList>('/id-cards/designs', { audience, ...params })).data!,

  createDesign: async (audience: IdCardAudience, input: IdCardDesignInput): Promise<IdCardDesign> =>
    (await apiService.post<IdCardDesign>('/id-cards/designs', { audience, ...input })).data!,

  updateDesign: async (id: string, input: IdCardDesignInput): Promise<IdCardDesign> =>
    (await apiService.patch<IdCardDesign>(`/id-cards/designs/${id}`, input)).data!,

  duplicateDesign: async (id: string): Promise<IdCardDesign> =>
    (await apiService.post<IdCardDesign>(`/id-cards/designs/${id}/duplicate`)).data!,

  makeDefault: async (id: string): Promise<IdCardDesign> =>
    (await apiService.post<IdCardDesign>(`/id-cards/designs/${id}/default`)).data!,

  deleteDesign: async (id: string): Promise<void> => { await apiService.delete(`/id-cards/designs/${id}`); },

  /** Enrolled students, as the enrollments list shows them. */
  listStudents: async (filter: ListEnrollmentsFilter): Promise<{ rows: EnrollmentListRow[]; pagination?: Pagination }> => {
    const r = await apiService.get<EnrollmentListRow[]>('/id-cards/students', buildListParams({ status: 'enrolled', ...filter }));
    return { rows: r.data ?? [], pagination: (r as any).pagination };
  },

  /** Active employees, as the employees list shows them. */
  listStaff: async (params: { search?: string; role?: string; page?: number; limit?: number }): Promise<{ users: User[]; pagination: Pagination }> =>
    (await apiService.get<{ users: User[]; pagination: Pagination }>('/id-cards/staff', { ...params, isActive: true })).data!,

  staffRoles: async (): Promise<Array<{ _id: string; name: string }>> =>
    (await apiService.get<Array<{ _id: string; name: string }>>('/id-cards/staff/roles')).data ?? [],
};
